import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useReveal } from "@/lib/motion";
import { PORTFOLIO } from "@/lib/siteData";
import { ClientFolder, FolderClipPath } from "./ClientFolder";

// Frame 17:100 — the studio pitch on orange, then the portfolio grid.
//
// The signature shape: a 2127px white circle centred on the frame's axis (17:111)
// bites a concave curve out of the base of the orange band. A border-radius
// cannot bend that way, so the circle is real.
//
// Every measurement below is a percentage of WIDTH, never a fixed pixel height.
// That matters: the circle's width scales with the viewport, so a fixed `top`
// would let the curve drift off the band's base at any width but 1440. In CSS,
// percentage margins resolve against the container's width — so anchoring the
// circle at `top: 100%` and pulling it back up with a percentage margin keeps the
// curve welded to the base of the band at every width.
//
// Derived from the frame (1440 wide, band 692 tall, circle top at y=514):
//   circle width      2127 / 1440 = 147.7%
//   curve depth       (692 - 406) / 1440 = 19.86%   how far it rises above the base
//   band bottom pad   (692 - 317) / 1440 = 26.04%   text ends at 317, so this clears the curve
// On a narrow viewport the curve's depth shrinks with the width while the text
// wraps to more lines, leaving only ~22px of clearance — enough to collide if the
// copy ever grows.
//
// The extra padding was 40px below md. It is now 90px below lg, because of who is
// looking: below lg the folders are on a phone or a tablet, where there is no
// hover, so every fan is open all the time rather than one at a time on demand.
// The band has to hold the open state permanently there, and 40px does not.
const BAND_PADDING = "pb-[calc(26.04%+90px)] lg:pb-[26.04%]";

const CIRCLE_WIDTH = "147.7%";
const CURVE_DEPTH = "-19.86%";

// How far the first row rises into the orange band. The frame draws 22.36% and
// that is what square cards used, but the folders open a fan of photos UPWARDS
// out of their own box, and that fan has to fit under the headline.
//
// Measuring it. A photo is 45% of the card's width at 4:5, which is 0.72 of the
// card's HEIGHT, and it starts 6% above the card and lifts 70% of itself. The
// outer cards also tilt 14 degrees, and tipping a tall box lifts its high corner
// another 0.05 of the card's height:
//
//   fan above the card's top = cardH x (0.06 + 0.70 x 0.72 + 0.05) = 0.61 x cardH
//   cardH                    = cardW / 1.273
//   headroom in the band     = 0.2604 W  (its bottom padding, plus 90px below lg)
//
// At 1440: cardW 415 -> cardH 326 -> the fan reaches 199px above the card. The
// band offers 375px, so 8% of W (115px) of overlap still leaves 61px of daylight
// under the headline. At 1024 the same sum leaves 57px. Below lg the cards are
// capped in px rather than scaling, so the fan is a fixed ~150-180px and the
// extra 90px of band padding covers it with the overlap at zero.
//
// The overlap is therefore a breakpoint decision, not a constant: the narrower
// the viewport, the wider each card, the taller the fan, and the less band there
// is left to climb into. Raise OPEN_DY in ClientFolder and this comes down.
const GRID_OVERLAP = "mt-0 lg:mt-[-5%]";
const ROW_GAP = "gap-x-6 gap-y-8 sm:gap-y-10 md:gap-x-8 md:gap-y-12 lg:gap-x-8 lg:gap-y-12";
const GRID_PADDING = "px-4 sm:px-6 md:px-8";

// Per-card delay, in reading order.
const CARD_STAGGER = 0.08;

export const Portfolio = () => {
    const reveal = useReveal();

    const folders = PORTFOLIO.folders;
    const hasFolders = folders.length > 0;

    return (
        <section id="portfolio" className="relative overflow-hidden bg-paper">
            {/* Orange band */}
            <div className={`relative bg-brand px-6 pt-[53px] ${BAND_PADDING}`}>
                <div className="relative z-10 mx-auto max-w-[1340px] text-center">
                    <motion.h2
                        className="type-display mx-auto max-w-[813px] text-balance text-[clamp(32px,3.8vw,55px)] text-paper"
                        {...reveal()}
                    >
                        {PORTFOLIO.headline}
                    </motion.h2>
                    <motion.p
                        className="mx-auto mt-6 max-w-[571px] text-[clamp(16px,1.7vw,24px)] text-paper"
                        {...reveal(0.08)}
                    >
                        {PORTFOLIO.body.map((run) =>
                            run.bold ? (
                                <strong key={run.text} className="font-bold">
                                    {run.text}
                                </strong>
                            ) : (
                                <span key={run.text}>{run.text}</span>
                            ),
                        )}
                    </motion.p>

                    {!hasFolders && (
                        <motion.div className="mt-12 flex justify-center" {...reveal(0.16)}>
                            <Link
                                to="/contato"
                                className="type-display inline-flex h-[50px] w-[269px] items-center justify-center bg-paper text-action text-brand btn-sweep [--sweep:theme(colors.brand.DEFAULT/12%)]"
                            >
                                {PORTFOLIO.action}
                            </Link>
                        </motion.div>
                    )}
                </div>

                <div
                    aria-hidden
                    className="absolute left-1/2 top-full aspect-square -translate-x-1/2 rounded-full bg-paper"
                    style={{ width: CIRCLE_WIDTH, marginTop: CURVE_DEPTH }}
                />
            </div>

            {hasFolders && (
                <div className={`relative mx-auto max-w-[1240px] pb-[45px] ${GRID_PADDING}`}>
                    <ul className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${ROW_GAP} ${GRID_OVERLAP}`}>
                        {folders.map((folder, index) => (
                            <motion.li
                                key={folder.id}
                                className="mx-auto w-full max-w-[340px] sm:max-w-[360px] md:max-w-[375px]"
                                {...reveal(index * CARD_STAGGER, { scale: true })}
                            >
                                <ClientFolder folder={folder} />
                            </motion.li>
                        ))}
                    </ul>

                    <div className="mt-[51px] flex justify-center">
                        <motion.div {...reveal()}>
                            <Link
                                to="/contato"
                                className="type-display inline-flex h-[50px] w-[269px] items-center justify-center bg-brand text-action text-paper btn-sweep [--sweep:theme(colors.brand.deep)]"
                            >
                                {PORTFOLIO.action}
                            </Link>
                        </motion.div>
                    </div>
                </div>
            )}
        </section>
    );
};

